// Dashboard.js — Startpagina na login
// Toont alle onboarde gemeenten per land en biedt zoeken voor nieuwe gemeente.
// Props:
//   gemeenten      — object { id: gemeenteObject } zoals al beheerd in AppWithOnboarding
//   dbStatus       — 'online' | 'offline' | 'laden'
//   onSelectGemeente(id)    — callback bij klik op bestaande gemeente
//   onStartOnboarding()     — callback bij klik op "Gemeente onboarden"
//   onVerversGemeenten()    — optionele callback om lijst te herfetchen

import React, { useState, useMemo, useEffect } from 'react';
import DataBronnen from './DataBronnen';
import { getGemeentenLijst } from './api';

// ── Kleurpalet ─────────────────────────────────────────────────────────────
const C = {
  ink:     '#12161C',   // donkere topbar, primaire tekst
  ink2:    '#3D4652',   // bodytekst
  ink3:    '#6B7684',   // secundaire tekst
  ink4:    '#9AA4B1',   // labels, meta
  paper:   '#FFFFFF',   // kaarten
  paper2:  '#FBFAF7',   // subtiele vlakken binnen kaarten
  canvas:  '#ECEAE5',   // paginaachtergrond (warm, geeft contrast met wit)
  line:    '#D9D5CD',   // binnenlijnen
  line2:   '#C4BFB5',   // kaartranden
  head:    '#F6F4F0',   // tabelkoppen, statsbalk
  accent:  '#1F5C6B',   // diep petrol: labels, actieve tab
  accent2: '#2D7F92',   // helderder petrol: links, hover-accent
  ok:      '#3F7D4E',
  okBg:    '#E4EEE2',
  okBd:    '#C9DDC5',
  warn:    '#9C6420',
  warnBg:  '#F7EBD8',
  warnBd:  '#E8D3AC',
  red:     '#B4442F',
};

// ── Bekende Vlaamse gemeenten voor de zoekfunctie ─────────────────────────
// Fallback-shortlist. De volledige lijst komt live uit de backend
// (nis-lookup.js, 581 gemeenten) via getGemeentenLijst.
const VLAAMSE_GEMEENTEN = [
  { naam: 'Aalst',        provincie: 'Oost-Vlaanderen',  nis: '41002' },
  { naam: 'Aalter',       provincie: 'Oost-Vlaanderen',  nis: '44084' },
  { naam: 'Aarschot',     provincie: 'Vlaams-Brabant',   nis: '24002' },
  { naam: 'Antwerpen',    provincie: 'Antwerpen',         nis: '11002' },
  { naam: 'Beveren',      provincie: 'Oost-Vlaanderen',  nis: '46003' },
  { naam: 'Bonheiden',    provincie: 'Antwerpen',         nis: '12005' },
  { naam: 'Boom',         provincie: 'Antwerpen',         nis: '11005' },
  { naam: 'Brasschaat',   provincie: 'Antwerpen',         nis: '11008' },
  { naam: 'Brugge',       provincie: 'West-Vlaanderen',  nis: '31005' },
  { naam: 'Dendermonde',  provincie: 'Oost-Vlaanderen',  nis: '42006' },
  { naam: 'Diest',        provincie: 'Vlaams-Brabant',   nis: '24020' },
  { naam: 'Gent',         provincie: 'Oost-Vlaanderen',  nis: '44021' },
  { naam: 'Genk',         provincie: 'Limburg',           nis: '71016' },
  { naam: 'Hasselt',      provincie: 'Limburg',           nis: '71022' },
  { naam: 'Heist-op-den-Berg', provincie: 'Antwerpen',   nis: '12014' },
  { naam: 'Knokke-Heist', provincie: 'West-Vlaanderen',  nis: '31043' },
  { naam: 'Kortrijk',     provincie: 'West-Vlaanderen',  nis: '34022' },
  { naam: 'Leuven',       provincie: 'Vlaams-Brabant',   nis: '24062' },
  { naam: 'Mechelen',     provincie: 'Antwerpen',         nis: '12025' },
  { naam: 'Mol',          provincie: 'Antwerpen',         nis: '13025' },
  { naam: 'Ninove',       provincie: 'Oost-Vlaanderen',  nis: '41048' },
  { naam: 'Olen',         provincie: 'Antwerpen',         nis: '13029' },
  { naam: 'Oostende',     provincie: 'West-Vlaanderen',  nis: '35013' },
  { naam: 'Roeselare',    provincie: 'West-Vlaanderen',  nis: '36015' },
  { naam: 'Sint-Niklaas', provincie: 'Oost-Vlaanderen',  nis: '46021' },
  { naam: 'Sint-Truiden', provincie: 'Limburg',           nis: '71053' },
  { naam: 'Temse',        provincie: 'Oost-Vlaanderen',  nis: '46025' },
  { naam: 'Turnhout',     provincie: 'Antwerpen',         nis: '13040' },
  { naam: 'Vilvoorde',    provincie: 'Vlaams-Brabant',   nis: '23088' },
  { naam: 'Waregem',      provincie: 'West-Vlaanderen',  nis: '34040' },
];

const NEDERLANDSE_GEMEENTEN = [
  { naam: 'Amsterdam',    provincie: 'Noord-Holland',    nis: 'GM0363' },
  { naam: 'Breda',        provincie: 'Noord-Brabant',    nis: 'GM0758' },
  { naam: 'Den Haag',     provincie: 'Zuid-Holland',     nis: 'GM0518' },
  { naam: 'Eindhoven',    provincie: 'Noord-Brabant',    nis: 'GM0772' },
  { naam: 'Groningen',    provincie: 'Groningen',         nis: 'GM0014' },
  { naam: 'Rotterdam',    provincie: 'Zuid-Holland',     nis: 'GM0599' },
  { naam: 'Tilburg',      provincie: 'Noord-Brabant',    nis: 'GM0855' },
  { naam: 'Utrecht',      provincie: 'Utrecht',           nis: 'GM0344' },
  { naam: 'Almere',       provincie: 'Flevoland',         nis: 'GM0034' },
  { naam: 'Nijmegen',     provincie: 'Gelderland',        nis: 'GM0268' },
  { naam: 'Arnhem',       provincie: 'Gelderland',        nis: 'GM0202' },
  { naam: 'Haarlem',      provincie: 'Noord-Holland',    nis: 'GM0392' },
];

// ── Compleetheidscontrole ──────────────────────────────────────────────────
// BEWUST BEPERKT tot inwoners en voertuigen. Dat zijn de enige twee velden die
// op de startpagina betrouwbaar te controleren zijn EN die de prognose fataal
// blokkeren als ze nul zijn: het model rekent dan met een leeg wagenpark.
// Beide verdwijnen automatisch uit de actielijst zodra ze in de editor worden
// bijgewerkt, want de waarde komt gewoon uit de gemeentenlijst.
//
// NIET gecontroleerd, en waarom niet:
//   postcodes   — worden automatisch gevuld bij POST /gemeenten uit de NIS-tabel
//   wijken      — komen automatisch uit de onboarding, en staan niet in de
//                 lichte gemeentenlijst (alleen in GET /gemeenten/:id)
//   privePct    — de backend vult 0,5 in als er niets in de database staat, dus
//                 een echte en een aangevulde waarde zijn hier niet te
//                 onderscheiden. Niet aan raken: de κ-correctie en de V5-
//                 rekenkern hangen hieraan.
function checkCompleet(g) {
  const acties = [];
  if (!(g.inwoners > 0))   acties.push('inwoners');
  if (!(g.voertuigen > 0)) acties.push('voertuigen');

  const compleet = acties.length === 0;

  // Bij een complete gemeente géén regel: de groene badge zegt het al, en zeven
  // keer dezelfde tekst onder elkaar maakt de lijst onrustig. Alleen gemeenten
  // die aandacht nodig hebben krijgen een regel, en vallen daardoor op.
  const samenvatting = compleet
    ? null
    : `Actie: ${acties.join(' en ')} invullen via Bewerken`;

  return { compleet, acties, samenvatting };
}

// ── Stijlen ────────────────────────────────────────────────────────────────
const st = {
  page: {
    minHeight: '100vh',
    background: C.canvas,
    color: C.ink,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', system-ui, sans-serif",
  },

  // Topbar
  // Rechterkant van de donkere balk: knop plus statusmelding
  topRechts: { marginLeft:'auto', display:'flex', alignItems:'center', gap:14 },
  // Zelfde omlijnde stijl als Bewerken en Verwijderen op de gemeentepagina
  bronKnop: { display:'flex', alignItems:'center', gap:6, fontSize:12.5,
              color:'rgba(255,255,255,0.72)', padding:'6px 12px',
              border:'1px solid rgba(255,255,255,0.16)', borderRadius:7,
              background:'transparent', cursor:'pointer', fontFamily:'inherit',
              whiteSpace:'nowrap' },
  topbar: {
    background: C.ink,
    height: 56,
    display: 'flex',
    alignItems: 'center',
    padding: '0 32px',
  },
  brand: { display: 'flex', alignItems: 'center', gap: 10 },
  brandVak: {
    background: C.accent2,
    color: '#fff',
    fontSize: 14, fontWeight: 600,
    letterSpacing: '-0.01em',
    borderRadius: 6,
    padding: '5px 12px',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  dbBadge: (status) => ({
    display: 'flex', alignItems: 'center', gap: 7,
    fontSize: 12,
    color: 'rgba(255,255,255,0.62)',
  }),
  dbDot: (status) => ({
    width: 6, height: 6, borderRadius: '50%',
    background: status === 'online' ? '#4BA85F' : status === 'offline' ? C.red : '#D89A3C',
    boxShadow: `0 0 0 3px ${status === 'online' ? 'rgba(75,168,95,0.18)' : status === 'offline' ? 'rgba(180,68,47,0.18)' : 'rgba(216,154,60,0.18)'}`,
    flexShrink: 0,
  }),

  // Header
  head: { padding: '36px 32px 0', maxWidth: 1180, margin: '0 auto' },
  eyebrow: {
    fontSize: 14, fontWeight: 700, letterSpacing: '0.1em',
    textTransform: 'uppercase', color: C.accent, marginBottom: 12,
  },
  h1: {
    fontSize: 31, fontWeight: 700, color: C.ink,
    letterSpacing: '-0.028em', lineHeight: 1.1, marginBottom: 8,
  },
  lede: { fontSize: 14.5, color: C.ink3, maxWidth: 520, lineHeight: 1.5 },

  // Landtabs
  tabs: {
    display: 'inline-flex', marginTop: 26,
    border: `1px solid ${C.line2}`, borderRadius: 9,
    overflow: 'hidden', background: C.paper,
  },
  tab: (active, first) => ({
    padding: '9px 20px',
    fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
    color: active ? '#fff' : C.ink3,
    background: active ? C.accent : C.paper,
    border: 'none',
    borderLeft: first ? 'none' : `1px solid ${active ? C.accent : C.line}`,
    fontFamily: 'inherit',
    display: 'flex', alignItems: 'center', gap: 7,
    transition: 'all 0.14s',
  }),
  flag: (active) => ({
    fontSize: 11, fontWeight: 700,
    opacity: active ? 0.62 : 0.5,
    letterSpacing: '0.02em',
  }),

  // Body
  body: { padding: '22px 32px 60px', maxWidth: 1180, margin: '0 auto' },
  mailKnop: { display:'inline-flex', alignItems:'center', gap:8, marginTop:16,
              padding:'9px 16px', borderRadius:8, border:`1px solid ${C.line2}`,
              background:C.paper, color:C.ink, fontSize:13.5, fontWeight:600,
              textDecoration:'none', cursor:'pointer' },
  grid: (tweeKolommen = true) => ({ display: 'grid',
    gridTemplateColumns: tweeKolommen ? '1.45fr 1fr' : '1fr', gap: 22, alignItems: 'start' }),

  // Kaart
  card: {
    background: C.paper,
    border: `1px solid ${C.line2}`,
    borderRadius: 12,
    boxShadow: '0 1px 2px rgba(18,22,28,0.05), 0 4px 14px rgba(18,22,28,0.045)',
    overflow: 'hidden',
  },
  cardHead: {
    padding: '16px 20px',
    borderBottom: `1px solid ${C.line}`,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: `linear-gradient(180deg, #FDFCFA 0%, ${C.paper} 100%)`,
  },
  cardTtl: { fontSize: 14, fontWeight: 700, color: C.ink, letterSpacing: '-0.01em' },

  // Kolomkoppen
  colHead: {
    display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 14,
    padding: '9px 20px',
    background: C.head,
    borderBottom: `1px solid ${C.line}`,
  },
  ch: {
    fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
    textTransform: 'uppercase', color: C.ink4,
  },

  // Rij
  row: (compleet, hover) => ({
    display: 'grid', gridTemplateColumns: '1fr auto auto',
    alignItems: 'center', gap: 14,
    padding: '13px 20px 13px 17px',
    borderBottom: `1px solid #EFECE6`,
    borderLeft: `3px solid ${hover ? (compleet ? C.ok : C.accent2) : 'transparent'}`,
    background: hover ? '#FAF8F4' : 'transparent',
    cursor: 'pointer',
    transition: 'all 0.12s',
  }),
  rowNm: { fontSize: 14.5, fontWeight: 600, color: C.ink, letterSpacing: '-0.01em' },
  rowMeta: { fontSize: 11.5, color: C.ink4, marginTop: 2 },
  badge: (ok) => ({
    fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 5,
    letterSpacing: '0.01em', whiteSpace: 'nowrap',
    background: ok ? C.okBg : C.warnBg,
    color: ok ? C.ok : C.warn,
    border: `1px solid ${ok ? C.okBd : C.warnBd}`,
  }),
  openBtn: {
    fontSize: 12.5, fontWeight: 600, color: C.accent2,
    display: 'flex', alignItems: 'center', gap: 5,
    background: 'none', border: 'none', cursor: 'pointer',
    fontFamily: 'inherit', padding: 0,
  },

  // Stats
  stats: {
    display: 'grid', gridTemplateColumns: '1fr 1fr 1fr',
    borderTop: `1px solid ${C.line}`,
    background: C.head,
  },
  stat: (last) => ({
    padding: '15px 20px',
    borderRight: last ? 'none' : `1px solid ${C.line}`,
  }),
  statV: (kleur) => ({
    fontSize: 23, fontWeight: 700, lineHeight: 1,
    letterSpacing: '-0.03em',
    fontVariantNumeric: 'tabular-nums',
    color: kleur || C.ink,
  }),
  statL: {
    fontSize: 10.5, fontWeight: 700, letterSpacing: '0.09em',
    textTransform: 'uppercase', color: C.ink4, marginTop: 5,
  },

  // Zoeken
  searchBox: { padding: 20 },
  searchWrap: { position: 'relative' },
  searchInput: (focus) => ({
    width: '100%',
    background: focus ? C.paper : C.paper2,
    border: `1px solid ${focus ? C.accent2 : C.line2}`,
    borderRadius: 9,
    padding: '12px 14px 12px 40px',
    fontSize: 14, color: C.ink,
    fontFamily: 'inherit', outline: 'none',
    boxSizing: 'border-box',
    boxShadow: focus ? '0 0 0 3px rgba(45,127,146,0.11)' : 'none',
    transition: 'all 0.14s',
  }),
  searchIcon: {
    position: 'absolute', left: 14, top: '50%',
    transform: 'translateY(-50%)',
    color: C.ink4, pointerEvents: 'none',
  },

  hint: {
    padding: '26px 20px', textAlign: 'center',
    fontSize: 13, color: C.ink4,
    borderTop: `1px solid ${C.line}`,
  },
  errMsg: {
    padding: '26px 20px', textAlign: 'center',
    fontSize: 13, color: C.red, lineHeight: 1.5,
    borderTop: `1px solid ${C.line}`,
  },

  // Zoekresultaat
  result: {
    padding: '18px 20px',
    borderTop: `1px solid ${C.line}`,
    background: C.paper2,
  },
  resNm: { fontSize: 19, fontWeight: 700, color: C.ink, letterSpacing: '-0.02em', marginBottom: 3 },
  resMeta: { fontSize: 12.5, color: C.ink3, marginBottom: 14 },
  resGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9, marginBottom: 15 },
  resCell: {
    background: C.paper, border: `1px solid ${C.line}`,
    borderRadius: 8, padding: '10px 13px',
  },
  resCl: {
    fontSize: 9.5, fontWeight: 700, letterSpacing: '0.1em',
    textTransform: 'uppercase', color: C.ink4, marginBottom: 4,
  },
  resCv: { fontSize: 14.5, fontWeight: 600, color: C.ink },
  onboardBtn: (hover) => ({
    width: '100%', padding: 12,
    background: hover ? C.accent : C.ink,
    border: 'none', borderRadius: 9, color: '#fff',
    fontSize: 14, fontWeight: 700, cursor: 'pointer',
    fontFamily: 'inherit',
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    letterSpacing: '-0.01em',
    transition: 'background 0.14s',
  }),

  // Info
  info: {
    padding: '16px 20px',
    borderTop: `1px solid ${C.line}`,
    background: '#F2F6F7',
    borderLeft: `3px solid ${C.accent2}`,
  },
  infoT: { fontSize: 12.5, fontWeight: 700, color: C.accent, marginBottom: 5 },
  infoX: { fontSize: 12.5, color: C.ink2, lineHeight: 1.6 },

  empty: {
    padding: '36px 20px', textAlign: 'center',
    color: C.ink3, fontSize: 13.5, lineHeight: 1.7,
  },
};

// ── Component ──────────────────────────────────────────────────────────────
export default function Dashboard({ gemeenten = {}, dbStatus = 'laden', onSelectGemeente,
                                    onStartOnboarding, gebruiker = null, onHoofdmenu = null }) {
  // Onboarden is werk van de beheerder. Voor een gewone gebruiker verdwijnt het
  // hele zoekblok, niet alleen de knop: ook de uitleg erover hoort hij niet te
  // zien. Zonder gebruiker gedraagt het scherm zich als voorheen.
  const magOnboarden = !gebruiker || gebruiker.rol === 'beheerder';

  // De landknoppen die deze gebruiker ziet. Een beheerder ziet altijd beide.
  const zichtbareLanden = (!gebruiker || gebruiker.rol === 'beheerder')
    ? ['BE', 'NL']
    : (gebruiker.landen && gebruiker.landen.length ? gebruiker.landen : ['BE']);
  // Beheerpagina met de gegevensbestanden die handmatig worden ingelezen.
  // Alleen voor eigen gebruik; gemeenten zien deze startpagina niet.
  const [toonBronnen, setToonBronnen] = useState(false);
  const [land, setLand] = useState(zichtbareLanden[0]);
  const [zoekTekst, setZoekTekst] = useState('');
  const [geselecteerdMatch, setGeselecteerdMatch] = useState(null);
  const [hoverRij, setHoverRij] = useState(null);
  const [zoekFocus, setZoekFocus] = useState(false);
  const [btnHover, setBtnHover] = useState(false);
  // Dynamische Vlaamse-gemeentenlijst uit backend (nis-lookup.js).
  // Bij pagina-load eenmalig opgehaald. Faalt de call, dan valt de zoekbalk
  // terug op de statische VLAAMSE_GEMEENTEN shortlist hierboven.
  const [dynVlaamseGemeenten, setDynVlaamseGemeenten] = useState(null);

  useEffect(() => {
    let actief = true;
    getGemeentenLijst('België')
      .then(d => { if (actief && Array.isArray(d?.gemeenten)) setDynVlaamseGemeenten(d.gemeenten); })
      .catch(() => { /* stil: fallback op hardcoded lijst */ });
    return () => { actief = false; };
  }, []);

  // Filter onboarde gemeenten per land, alfabetisch gesorteerd
  const onboardeGemeenten = useMemo(() => {
    return Object.values(gemeenten)
      .filter(g => {
        const isNL = g.land === 'Nederland';
        return land === 'NL' ? isNL : !isNL;
      })
      .sort((a, b) => (a.naam || '').localeCompare(b.naam || '', 'nl'));
  }, [gemeenten, land]);

  // Compleetheid per gemeente, één keer berekend
  const metStatus = useMemo(
    () => onboardeGemeenten.map(g => ({ g, status: checkCompleet(g) })),
    [onboardeGemeenten]
  );

  const compleetAantal   = metStatus.filter(x => x.status.compleet).length;
  const incompleetAantal = metStatus.length - compleetAantal;

  // Zoeklogica: match op naam (startsWith, min. 2 tekens).
  // BE gebruikt dynamische lijst als beschikbaar, anders de hardcoded fallback.
  const zoekMatch = useMemo(() => {
    if (!zoekTekst || zoekTekst.length < 2) return null;
    const lijst = land === 'NL'
      ? NEDERLANDSE_GEMEENTEN
      : (dynVlaamseGemeenten && dynVlaamseGemeenten.length ? dynVlaamseGemeenten : VLAAMSE_GEMEENTEN);
    // Sluit al onboarde gemeenten uit
    const onboardeNamen = new Set(Object.values(gemeenten).map(g => g.naam.toLowerCase()));
    return lijst.find(g =>
      g.naam.toLowerCase().startsWith(zoekTekst.toLowerCase()) &&
      !onboardeNamen.has(g.naam.toLowerCase())
    ) || null;
  }, [zoekTekst, land, gemeenten, dynVlaamseGemeenten]);

  const heeftGeenMatch = zoekTekst.length >= 2 && !zoekMatch;

  function handleZoekChange(e) {
    setZoekTekst(e.target.value);
    setGeselecteerdMatch(null);
  }

  function handleOnboard() {
    if (onStartOnboarding) onStartOnboarding(zoekMatch);
  }

  const landNaam = land === 'BE' ? 'België' : 'Nederland';

  if (toonBronnen && magOnboarden) {
    return <DataBronnen onClose={() => setToonBronnen(false)} />;
  }

  return (
    <div style={st.page}>

      {/* Topbar */}
      <header style={st.topbar}>
        <div style={st.brand}>
          <div style={st.brandVak}>LaadAtlas</div>
          {/* Alleen voor de beheerder: terug naar de startpagina met de modules. */}
          {onHoofdmenu && (
            <button style={st.bronKnop} onClick={onHoofdmenu}>Hoofdmenu</button>
          )}
        </div>
        <div style={st.topRechts}>
          {/* De gegevensbronnen gelden voor alle gemeenten samen en worden door
              de beheerder ingelezen. Een gewone gebruiker hoort die pagina niet
              te zien, ook niet als de server de inhoud toch zou weigeren. */}
          {magOnboarden && (
          <button style={st.bronKnop} onClick={() => setToonBronnen(true)}>
            <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
              <ellipse cx="8" cy="3.5" rx="5.5" ry="2.2"/>
              <path d="M2.5 3.5v9c0 1.2 2.5 2.2 5.5 2.2s5.5-1 5.5-2.2v-9"/>
              <path d="M2.5 8c0 1.2 2.5 2.2 5.5 2.2s5.5-1 5.5-2.2"/>
            </svg>
            Data bronnen
          </button>
          )}
          {/* De stand van de database zegt een klant niets en hoort bij het
              beheer. Bij een storing komt er wel een melding voor iedereen. */}
          {magOnboarden ? (
            <div style={st.dbBadge(dbStatus)}>
              <span style={st.dbDot(dbStatus)} />
              Database {dbStatus}
            </div>
          ) : dbStatus === 'offline' ? (
            <div style={st.dbBadge(dbStatus)}>
              <span style={st.dbDot(dbStatus)} />
              Server niet bereikbaar
            </div>
          ) : null}
        </div>
      </header>

      {/* Header */}
      <div style={st.head}>
        <div style={st.eyebrow}>Laadinfrastructuur planning</div>
        <h1 style={st.h1}>{magOnboarden ? 'Gemeenten' : 'Welkom bij LaadAtlas'}</h1>
        <div style={st.lede}>
          {magOnboarden
            ? 'Open een gemeente om de prognose en wijkanalyse te bekijken, of voeg een nieuwe gemeente toe aan het portfolio.'
            : 'Open een gemeente om de prognose en wijkanalyse te bekijken.'}
        </div>
        {!magOnboarden && (
          <a href={`mailto:${land === 'NL' ? 'info@laadatlas.nl' : 'info@laadatlas.be'}`}
             style={st.mailKnop}>
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"
                 stroke="currentColor" strokeWidth="1.6">
              <rect x="1.5" y="3" width="13" height="10" rx="1.5" />
              <path d="M1.5 4.5 8 9l6.5-4.5" />
            </svg>
            Vraag of opmerking
          </a>
        )}

        {zichtbareLanden.length > 1 && (
          <div style={st.tabs}>
            <button
              style={st.tab(land === 'BE', true)}
              onClick={() => { setLand('BE'); setZoekTekst(''); }}
            >
              <span style={st.flag(land === 'BE')}>BE</span>België
            </button>
            <button
              style={st.tab(land === 'NL', false)}
              onClick={() => { setLand('NL'); setZoekTekst(''); }}
            >
              <span style={st.flag(land === 'NL')}>NL</span>Nederland
            </button>
          </div>
        )}
      </div>

      {/* Body */}
      <div style={st.body}>
        <div style={st.grid(magOnboarden)}>

          {/* Linkerkolom: onboarde gemeenten */}
          <div style={st.card}>
            <div style={st.cardHead}>
              <div style={st.cardTtl}>Onboarde gemeenten</div>
            </div>

            {onboardeGemeenten.length === 0 ? (
              <div style={st.empty}>
                Nog geen gemeenten voor {landNaam}.<br />
                Gebruik het zoekvenster rechts om een gemeente toe te voegen.
              </div>
            ) : (
              <>
                <div style={st.colHead}>
                  <div style={st.ch}>Gemeente</div>
                  <div style={st.ch}>Status</div>
                  <div style={st.ch} />
                </div>

                {metStatus.map(({ g, status }, i) => (
                  <div
                    key={g.id}
                    style={{
                      ...st.row(status.compleet, hoverRij === g.id),
                      borderBottom: i === metStatus.length - 1 ? 'none' : '1px solid #EFECE6',
                    }}
                    onMouseEnter={() => setHoverRij(g.id)}
                    onMouseLeave={() => setHoverRij(null)}
                    onClick={() => onSelectGemeente && onSelectGemeente(g.id)}
                  >
                    <div>
                      <div style={st.rowNm}>{g.naam}</div>
                      {status.samenvatting && (
                        <div style={st.rowMeta}>{status.samenvatting}</div>
                      )}
                    </div>
                    <span style={st.badge(status.compleet)}>
                      {status.compleet ? 'Compleet' : 'Incompleet'}
                    </span>
                    <button
                      style={st.openBtn}
                      onClick={e => { e.stopPropagation(); onSelectGemeente && onSelectGemeente(g.id); }}
                    >
                      Open
                      <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8">
                        <path d="M2 7h9M7 3l4 4-4 4" />
                      </svg>
                    </button>
                  </div>
                ))}

                <div style={st.stats}>
                  <div style={st.stat(false)}>
                    <div style={st.statV()}>{onboardeGemeenten.length}</div>
                    <div style={st.statL}>Gemeenten</div>
                  </div>
                  <div style={st.stat(false)}>
                    <div style={st.statV(C.ok)}>{compleetAantal}</div>
                    <div style={st.statL}>Compleet</div>
                  </div>
                  <div style={st.stat(true)}>
                    <div style={st.statV(incompleetAantal > 0 ? C.warn : C.ink4)}>{incompleetAantal}</div>
                    <div style={st.statL}>Incompleet</div>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Rechterkolom: zoeken. Alleen voor de beheerder; een gewone
              gebruiker onboardt geen gemeenten en ziet ook de uitleg niet. */}
          {magOnboarden && (
          <div style={st.card}>
            <div style={st.cardHead}>
              <div style={st.cardTtl}>Gemeente toevoegen</div>
            </div>

            <div style={st.searchBox}>
              <div style={st.searchWrap}>
                <svg style={st.searchIcon} width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <circle cx="6.5" cy="6.5" r="4.5" /><path d="M10 10l3.5 3.5" />
                </svg>
                <input
                  style={st.searchInput(zoekFocus)}
                  type="text"
                  placeholder="Zoek op gemeentenaam..."
                  value={zoekTekst}
                  onChange={handleZoekChange}
                  onFocus={() => setZoekFocus(true)}
                  onBlur={() => setZoekFocus(false)}
                  autoComplete="off"
                />
              </div>
            </div>

            {/* States */}
            {!zoekTekst || zoekTekst.length < 2 ? (
              <div style={st.hint}>Typ de naam van een gemeente om te zoeken.</div>
            ) : heeftGeenMatch ? (
              <div style={st.errMsg}>
                Geen gemeente gevonden voor &ldquo;{zoekTekst}&rdquo;.<br />
                Controleer de spelling, of de gemeente is al toegevoegd.
              </div>
            ) : zoekMatch ? (
              <div style={st.result}>
                <div style={st.resNm}>{zoekMatch.naam}</div>
                <div style={st.resMeta}>
                  {zoekMatch.provincie || 'provincie onbekend'} &middot; NIS {zoekMatch.nis}
                </div>
                <div style={st.resGrid}>
                  <div style={st.resCell}>
                    <div style={st.resCl}>Provincie</div>
                    <div style={st.resCv}>{zoekMatch.provincie || '—'}</div>
                  </div>
                  <div style={st.resCell}>
                    <div style={st.resCl}>NIS-code</div>
                    <div style={st.resCv}>{zoekMatch.nis}</div>
                  </div>
                </div>
                <button
                  style={st.onboardBtn(btnHover)}
                  onMouseEnter={() => setBtnHover(true)}
                  onMouseLeave={() => setBtnHover(false)}
                  onClick={handleOnboard}
                >
                  <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.9">
                    <path d="M8 2v8M4.5 7l3.5 4 3.5-4" /><path d="M2.5 14h11" />
                  </svg>
                  {zoekMatch.naam} onboarden
                </button>
              </div>
            ) : null}

            <div style={st.info}>
              <div style={st.infoT}>Wat gebeurt er bij onboarden?</div>
              <div style={st.infoX}>
                De wizard haalt bevolkingscijfers, NIS-code, wijkgrenzen en bestaande laadpaaldata automatisch op.
                Daarna stel je de gemeente-specifieke parameters in. De analyse is direct beschikbaar zodra de wizard is afgerond.
              </div>
            </div>
          </div>
          )}

        </div>
      </div>
    </div>
  );
}
